<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-[1011] flex items-center justify-center bg-[rgba(6,6,7,0.7)] p-5 backdrop-blur-[3px]" @click="emit('minimize')">
      <div
        class="promo-modal-card relative w-full max-w-[420px] overflow-hidden rounded-[22px] border border-[color-mix(in_srgb,var(--white)_10%,transparent)] border-t-[color-mix(in_srgb,var(--white)_18%,transparent)] bg-gradient-to-b from-[#1a1a1d] to-[#0d0d0f] p-[clamp(22px,6vw,30px)_clamp(18px,5vw,26px)_26px] shadow-[0_30px_70px_rgba(0,0,0,0.55)]"
        @click.stop
      >
        <LayoutPromoCodeConfetti :shots="shots" />

        <button
          type="button"
          class="absolute right-[14px] top-[14px] flex size-[30px] items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--white)_6%,transparent)] text-[color-mix(in_srgb,var(--white)_50%,transparent)] transition-[background-color,color,transform] duration-150 ease-out hover:bg-[color-mix(in_srgb,var(--white)_12%,transparent)] hover:text-[color-mix(in_srgb,var(--white)_90%,transparent)] active:scale-[0.88]"
          aria-label="Minimize promo"
          @click="emit('minimize')"
        >
          <icon-promo-close class="text-[13px]" />
        </button>

        <span
          class="relative inline-block rounded-md border border-[#ff6a2e]/30 bg-[#ff6a2e]/[0.14] px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.6px] text-[#ff6a2e]"
        >
          {{ promoCode.badge }}
        </span>
        <div class="font-anton relative mt-3.5 text-[clamp(24px,7vw,32px)] font-normal leading-[1.1] tracking-[-0.01em] text-title-color">
          {{ promoCode.titlePrefix }} <span class="text-[#ff6a2e]">{{ promoCode.titleHighlight }}</span>
        </div>
        <div class="relative mt-1.5 text-[13.5px] font-medium text-paragraph-color">{{ promoCode.subText }}</div>

        <div class="relative mt-[22px] flex items-center gap-2.5">
          <button
            type="button"
            class="flex flex-1 items-center justify-center gap-2.5 rounded-xl border border-dashed border-[color-mix(in_srgb,var(--white)_22%,transparent)] bg-[color-mix(in_srgb,var(--white)_5%,transparent)] px-4 py-[13px] text-white transition-transform duration-150 ease-out hover:border-[color-mix(in_srgb,var(--white)_34%,transparent)] hover:bg-[color-mix(in_srgb,var(--white)_9%,transparent)] active:scale-[0.97]"
            :aria-label="`Copy promo code ${promoCode.code}`"
            @click="copyCode"
          >
            <span class="font-anton text-[18px] font-normal tracking-[1.5px]">{{ promoCode.code }}</span>
            <span class="flex size-[22px] shrink-0 items-center justify-center">
              <icon-promo-check v-if="copied" class="text-[17px] text-[#4ade80] [&_path]:!fill-none" />
              <icon-promo-copy v-else class="text-[16px] text-[color-mix(in_srgb,var(--white)_55%,transparent)] [&_path]:!fill-none" />
            </span>
          </button>
        </div>

        <Transition name="promo-toast">
          <div
            v-if="showToast"
            class="promo-toast absolute bottom-[26px] left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg border border-[color-mix(in_srgb,var(--white)_12%,transparent)] bg-[#1a1a1d] px-3 py-1.5 text-[12px] font-semibold text-white shadow-[0_8px_20px_rgba(0,0,0,0.4)]"
          >
            Code copied
          </div>
        </Transition>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import type { PromoCodeFeature } from "../../../types/templateConfiguration";
import type { PromoConfettiShot } from "./PromoCodeConfetti.vue";

const props = defineProps<{ promoCode: PromoCodeFeature }>();
const emit = defineEmits<{ minimize: [] }>();

const { copied, showToast, copyCode } = usePromoCodeClipboard(props.promoCode.code);

// paint-splat decoration positions, tuned for the compact centered card layout
const shots: PromoConfettiShot[] = [
  { top: 30, size: 10, travel: 300, arc: 26, delay: 0.5, colorFrom: "#ffb27a", colorMid: "#ff6a2e", colorTo: "#c94500", glow: "rgba(255,106,46,0.75)", splatTop: 10, splatRight: 130, splatSize: 48 },
  { top: 70, size: 9, travel: 250, arc: 40, delay: 0.85, colorFrom: "#fff2a8", colorMid: "#ffd23f", colorTo: "#b88900", glow: "rgba(255,210,63,0.75)", splatTop: 48, splatRight: 90, splatSize: 40 },
  { top: 14, size: 11, travel: 330, arc: 14, delay: 1.2, colorFrom: "#ff9fd6", colorMid: "#ff3fa4", colorTo: "#a30057", glow: "rgba(255,63,164,0.75)", splatTop: -8, splatRight: 30, splatSize: 52 },
];
</script>

<style scoped>
.promo-modal-card {
  animation: promo-modal-card-in 340ms cubic-bezier(0.16, 1, 0.3, 1) both;
}
.font-anton,
.font-anton * {
  font-family: "Anton", Impact, sans-serif;
}
@keyframes promo-modal-card-in {
  from {
    transform: scale(0.92);
    opacity: 0;
    filter: blur(6px);
  }
  to {
    transform: scale(1);
    opacity: 1;
    filter: blur(0);
  }
}
.promo-toast-enter-active {
  transition:
    opacity 180ms cubic-bezier(0.16, 1, 0.3, 1),
    transform 180ms cubic-bezier(0.16, 1, 0.3, 1);
}
.promo-toast-enter-from {
  opacity: 0;
  transform: translateY(6px) translateX(-50%);
}
@media (prefers-reduced-motion: reduce) {
  .promo-modal-card {
    animation-duration: 1ms;
  }
}
</style>
